/**
 * 상품 랭킹(market) React Query 키.
 *
 * ## 규약
 * - 키 모양은 `[도메인, 조회이름, …인자를 «값으로» 펼친 것]` — 객체를 통째로 넣지 않는다.
 * - `*Root()` 는 `resetQueries`/`removeQueries` 대상용 접두 키다.
 * - 이 도메인의 조회는 전부 **ES 질문**이라 `staleTime` 은 60초를 명시한다
 *   (같은 시그니처면 같은 결과라는 전제). 새 조회는 이 아래에 보탠다.
 *
 * ## ⚠️ 인자는 화면이 이미 쓰던 «시그니처 한 줄»을 그대로 쓴다
 * `scope/market-rankings-scope.ts` 의 `toBoardScopeKey`·`toSearchScopeKey` 가
 * 「다시 조회해야 하는가」를 판정하는 문자열이고, **캐시 정체성도 같은 문자열**이다.
 * 여기서 새로 만들지 않는다 — 만드는 자리가 둘이 되면 갈린다.
 *
 * ## ⚠️ 상품 좌표가 셋으로 갈린다 — 일부러다
 * `detail`·`history` 는 `detailRefFor` 좌표(`postId|source|kind`)를 쓰고, `crossChannel` 은
 * **`kind` 를 안 쓰고 `brand`·`title` 을 쓴다**. 「다른 채널의 같은 상품」을 찾는 질문이라
 * 이쪽 종류로 저쪽을 가리면 안 된다(`use-product-detail` 머리말). 좌표가 다르니 키도 갈라 둔다.
 *
 * ## ⚠️ 앞단에 `runCached`(TTL 없는 수동 무효화 캐시)가 따로 있다
 * `sections/market/utils/market-rankings-request-cache.ts` 가 질문 단위로 promise 를 공유한다. RQ 캐시와
 * **이중**이고, 값이 바뀐 것을 아는 자리(상세 수집 직후)의 무효화 경로는 `invalidateCache` 다.
 */
export const marketKeys = {
  all: ['market'] as const,

  // ── 일별 판매채널 보드 ──────────────────────────────────────────────────────
  boardRoot: () => [...marketKeys.all, 'board'] as const,
  /** @param scopeKey `toBoardScopeKey(filter)` — `{kind, date}` 를 JSON 으로 이은 것 */
  board: (scopeKey: string) => [...marketKeys.boardRoot(), scopeKey] as const,

  // ── 랭킹 DB 검색 ────────────────────────────────────────────────────────────
  searchRoot: () => [...marketKeys.all, 'search'] as const,
  /**
   * @param scopeKey `toSearchScopeKey(filter)` — 검색어·종류·채널·가격대·브랜드·상세만·정렬·태그
   *
   * ⚠️ 판매몰 «실시간» 검색(`runLiveSearch`)은 여기 없다 — 명령형 「지금 긁어라」라서 조회가 아니다.
   */
  search: (scopeKey: string) => [...marketKeys.searchRoot(), scopeKey] as const,

  // ── 상품 하나 ───────────────────────────────────────────────────────────────
  productRoot: () => [...marketKeys.all, 'product'] as const,
  /** @param detailKey `cacheKey('rankings.detail', detailRefFor(...))` — 앞단 캐시와 «같은» 문자열 */
  detail: (detailKey: string) => [...marketKeys.productRoot(), 'detail', detailKey] as const,
  /**
   * @param versionKey `cacheKey('rankings.detailVersion', {postId, source, seq})`
   *
   * ⚠️ 좌표에 `kind` 가 없다 — 버전 인덱스에 그 필드가 없다. 대신 `seq` 가 들어가
   * **버전마다 캐시가 따로** 선다(지난 버전은 불변이라 다시 물을 이유가 없다).
   */
  detailVersion: (versionKey: string) =>
    [...marketKeys.productRoot(), 'detail-version', versionKey] as const,
  /** @param historyKey `cacheKey('rankings.history', ref)` — 상세와 같은 좌표여야 한다 */
  history: (historyKey: string) => [...marketKeys.productRoot(), 'history', historyKey] as const,
  /** @param crossKey `cacheKey('rankings.crossChannel', {postId, source, brand, title})` — `kind` 가 없다 */
  crossChannel: (crossKey: string) => [...marketKeys.productRoot(), 'cross-channel', crossKey] as const,
};
